import { Alert, AlertIcon, Heading, Show, SimpleGrid, Text, VStack } from '@chakra-ui/react';
import { SWAP_MATCHDAY } from '@/constants';
import { AI_LEAGUE_SWAP_JUSTIFICATION_PLACEHOLDER } from '@/constants/aiLeague/aiLeagueContent';
import { formatAiResponseTime } from '@/constants/aiLeague/aiLeagueBrand';
import type { AiModelContent } from '@/constants/aiLeague/aiLeagueTypes';
import { applyListSwap } from '@/helpers/roster/applyListSwap';
import {
  swapJustificationForDisplay,
  swapPairToRankLabel,
  swappedDisplayRankIndices,
} from '@/helpers/leagues/mergeAiLeagueModels';
import type { AiLeagueLeaderboardEntry } from '@/hooks/useAiLeaguePageData';
import { pageMetaTextProps, pageSectionTitleProps } from '@/theme/sitePageTypography';
import { Card } from '@/views/LeaguePage/Card';
import { AiLeagueJustification } from './AiLeagueJustification';
import { AiLeagueListCard } from './AiLeagueRankedList';

function listCardTitle(hasSwap: boolean): string {
  return hasSwap ? `List from quarter-finals (MD${SWAP_MATCHDAY + 1}+)` : 'Final 10-team list';
}

function SwapDecisionCard(props: { model: AiModelContent }) {
  const { text, isPlaceholder } = swapJustificationForDisplay(
    props.model.swapJustificationText,
    AI_LEAGUE_SWAP_JUSTIFICATION_PLACEHOLDER,
  );

  return (
    <Card w='full' borderRadius='md' p={{ base: 3, md: 4 }}>
      <Text {...pageSectionTitleProps} mb={3}>
        Swap decision
      </Text>
      {isPlaceholder ? (
        <Text fontSize='sm' color='gray.500' fontStyle='italic' whiteSpace='pre-wrap'>
          {text}
        </Text>
      ) : (
        <AiLeagueJustification text={text} />
      )}
    </Card>
  );
}

export function AiModelTabPanel(props: {
  model: AiModelContent;
  tournamentStarted: boolean;
  leaderboardEntries: AiLeagueLeaderboardEntry[];
}) {
  const { model, tournamentStarted, leaderboardEntries } = props;
  const standing = leaderboardEntries.find((entry) => entry.id === model.id);
  const hasSwap = Boolean(model.listValid && model.listIndexSwapPair);
  const displayList = applyListSwap(model.countryList, model.listIndexSwapPair);
  const swappedRankIndices = swappedDisplayRankIndices(model.listIndexSwapPair);
  const showSwapDecision = Boolean(model.listValid && (hasSwap || model.swapJustificationText));
  const swapLabel = hasSwap
    ? `Swapped ranks ${swapPairToRankLabel(model.listIndexSwapPair!)} · effective MD${SWAP_MATCHDAY + 1}+`
    : null;

  let meta: string | null = null;
  if (!model.listValid) {
    meta = 'Invalid list · 0 pts in AI League';
  } else if (!tournamentStarted) {
    meta = 'Tournament not started';
  } else if (standing) {
    meta = `Rank ${standing.displayRank} · ${standing.currentScore} pts`;
  }

  const responseTimeLabel = `Response time: ${formatAiResponseTime(model.responseTimeSeconds)}`;
  const listCardProps = {
    countryList: displayList,
    listValid: model.listValid,
    swappedRankIndices,
    title: listCardTitle(hasSwap),
  };

  return (
    <VStack align='stretch' spacing={6} w='full'>
      <VStack align='stretch' spacing={1}>
        <Heading size='md' as='h2'>
          {model.displayName}
        </Heading>
        <Text {...pageMetaTextProps}>{responseTimeLabel}</Text>
        {meta ? <Text {...pageMetaTextProps}>{meta}</Text> : null}
        {swapLabel ? <Text {...pageMetaTextProps}>{swapLabel}</Text> : null}
      </VStack>

      {!model.listValid ? (
        <Alert status='warning' borderRadius='md' fontSize='sm'>
          <AlertIcon />
          This list breaks the game rules (too many seeds) and scores 0 in the AI League. It is shown
          exactly as submitted.
        </Alert>
      ) : null}

      <Show above='lg'>
        <SimpleGrid columns={2} spacing={4} w='full' alignItems='start'>
          <AiLeagueListCard {...listCardProps} />
          <Card w='full' borderRadius='md' p={{ base: 3, md: 4 }} h='full'>
            <Text {...pageSectionTitleProps} mb={3}>
              Original list explanation
            </Text>
            <AiLeagueJustification text={model.justificationText} />
          </Card>
        </SimpleGrid>
      </Show>

      <Show below='lg'>
        <VStack align='stretch' spacing={4} w='full'>
          <AiLeagueListCard {...listCardProps} />
          <Card w='full' borderRadius='md' p={{ base: 3, md: 4 }}>
            <Text {...pageSectionTitleProps} mb={3}>
              Original list explanation
            </Text>
            <AiLeagueJustification text={model.justificationText} />
          </Card>
        </VStack>
      </Show>

      {showSwapDecision ? <SwapDecisionCard model={model} /> : null}
    </VStack>
  );
}
